/* eslint-disable camelcase */
import { Injectable, WritableSignal, inject, signal } from '@angular/core'
import { Router } from '@angular/router'
import { CdcTokenResponse } from '@app/core/models/custom/auth.model'
import { AuthService, CmsService, EventService, OCC_USER_IDS, User } from '@spartacus/core'
import { UserAccountChangedEvent } from '@spartacus/user/account/root'
import { BehaviorSubject } from 'rxjs'
import { GlobalLoaderService } from './global-loader.service'

@Injectable({ providedIn: 'root' })
export class CustomAuthService extends AuthService {
  private readonly globalLoaderService = inject(GlobalLoaderService)
  private readonly cmsService = inject(CmsService)
  private readonly eventService = inject(EventService)
  private readonly router = inject(Router)
  private urlBeforeRedirect: WritableSignal<string | undefined> = signal(undefined)

  public authStateChange = new BehaviorSubject<boolean>(false)
  anonUserAccessingRestrictPage$ = new BehaviorSubject(false)

  setAuthToken (data: CdcTokenResponse): void {
    this.authStorageService.setToken({
      access_token: data.token.value,
      token_type: data.token.tokenType,
      refresh_token: data.token.refreshToken?.value ?? null,
      expires_at: data.token.expiration,
      granted_scopes: data.token.scope,
      access_token_stored_at: new Date().toISOString()
    })
  }

  finishAuthentication (data: CdcTokenResponse): void {
    this.setAuthToken(data)
    this.userIdService.setUserId(OCC_USER_IDS.OCC_USER_ID_CURRENT)
    this.globalLoaderService.setIsLoaderVisible(false)
    this.authStateChange.next(true)
    this.cmsService.refreshLatestPage()
  }

  isAuthStateChangeCompleted (): boolean {
    return this.authStateChange.value
  }

  updateApplicationUser (user: User): void {
    this.eventService.dispatch({ user }, UserAccountChangedEvent)
  }

  /**
   * Counterpart to `AuthRedirectService.saveCurrentNavigationUrl()`.
   * Saves the url of the page user attempted to access before login
   * Because homepage is not in auth flow and it always gets cleared by `AuthRedirectStorageService`.
   */
  saveUrlBeforeRedirect (): void {
    const navigation = this.router.getCurrentNavigation()
    if (navigation?.finalUrl) {
      const url = this.router.serializeUrl(navigation.finalUrl)
      this.urlBeforeRedirect.set(url)
    }
  }

  redirect (): void {
    const url = this.urlBeforeRedirect()

    if (url) {
      this.authRedirectService.setRedirectUrl(url)
      this.urlBeforeRedirect.set(undefined)
      this.authRedirectService.redirect()
    }
  }
}
